import { isEqual, omit, result, get } from 'underscore';
import React from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { withPropFilter } from '@bingads-webui-campaign-react/with-prop-filter';

import { withDefaultStyles } from '@bingads-webui-react/with-default-styles';
import { AllSettingsView } from '@bingads-webui-campaign/all-settings';
import { perfMarkerPropTypes } from '@bingads-webui/performance-marker';
import { withSiteMap, getRoutePathById } from '@bingads-webui-campaign-react/with-site-map';
import { EventDelegator } from '@bingads-webui-campaign-react/event-delegator';
import { getRouterDep } from '@bingads-webui-campaign-react/legacy-router-dep';
import { CampaignSubTypeMapping, isAudienceNativeAdsCampaign, isAudienceCampaign, isSearchShoppingCampaign, isAudienceShoppingCampaign, isSearchCampaign } from '@bingads-webui-campaign-react/constants';
import { CampaignType } from '@bingads-webui-campaign/campaign-ui-common';
import { ContentFooter } from '@bingads-webui-campaign-react/content-footer';
import { SponsoredCampaignSettingsPage } from '@bingads-webui-campaign-react/sponsored-campaign-creation';
import { isMSStoreAdsCampaign } from '@bingads-webui-campaign-react/ms-store-ads-common';
import { withCcJsData } from '@bingads-webui-cc-react/js-data-rest';

import {
  getScope,
  TargetingScope,
} from '@bingads-webui-campaign/targeting-settings-scaffolds';
import { BackboneInstanceWrapper } from '@bingads-webui-react/backbone-instance-wrapper';
import { HotelCampaignSettings } from '@bingads-webui-campaign/hotel-campaign-upsert';
import { HotelAdGroupSettings } from '@bingads-webui-campaign/hotel-adgroup-upsert';
import { PerfMaxCampaignSettingsPage } from '@bingads-webui-campaign-react/perf-max-step-wizard';
import { PerfMaxNorthStarSettingWizard } from '@bingads-webui-campaign-react/perf-max-wizard-north-star';
import { LinkedInCampaignSettingsWizard, LinkedInAdGroupSettingsWizard } from '@bingads-webui-campaign-react/linkedin-campaign-wizard';
import { DisplayPlusEditCampaignWizard, DisplayPlusEditAdGroupWizard } from '@bingads-webui-campaign-react/display-plus-campaigns';
import { XandrVideoCampaignSettingsPage, XandrVideoAdGroupSettingsPage } from '@bingads-webui-campaign-react/xandr-video-campaign';
import { XandrDisplayAdsAdGroupSettingsPage, XandrDisplayAdsCampaignSettingsPage } from '@bingads-webui-campaign-react/xandr-display-ads-campaign';
import { AppAdsCampaignEditPage } from '@bingads-webui-campaign-react/app-ads-campaigns';
import { UnifiedAppEditCampaignSettingsWizard } from '@bingads-webui-campaign-react/unified-app-campaign-wizard';
import { withCurrentExpandedScope } from '@bingads-webui-campaign-react/use-current-scope-entity';
import { StandardShoppingCampaignWizard, CREATE_SCENARIO } from '@bingads-webui-campaign-react/standard-shopping-wizard';
import { SearchCampaignEditWizard, SearchAdGroupEditWizard } from '@bingads-webui-campaign-react/search-campaign-wizard';
import { setClarityTag } from '@bingads-webui-universal/clarity';
import { getDeps } from './form-legacy-deps-list';
import { initializeWithLegacyDependencies } from './get-injected-dependencies';
import { redirectToAdsPageIfCurrentCampaignIsSSCAndUserIsInAdGroupLevel } from './ssc-redirect';
import { getSettingsPageStyles } from './styles/index';

const getIsNewShoppingWizard = ({ currentCampaign, permissions }) => {
  const isPAAIMConsolidation = get(permissions, ['dynamic', 'PAAIMConsolidation'], false);
  const isSearchShoppingWizard = get(permissions, ['dynamic', 'SearchShoppingWizard'], false);
  if (currentCampaign) {
    const { CampaignType: CType, CampaignSubType: SubCType } = currentCampaign;
    if (isSearchShoppingCampaign(CType, SubCType) && isSearchShoppingWizard) {
      return true;
    } else if (isAudienceShoppingCampaign(CType, SubCType) && isPAAIMConsolidation) {
      return true;
    }
  }
  return false;
};

const propFilter = (props) => {
  const { currentCampaign, currentAdGroup, permissions } = props;

  const formatCurrentCampaign = {
    ...currentCampaign,
    CampaignSubType: CampaignSubTypeMapping[currentCampaign.CampaignSubType],
  };

  const { campaignId, adGroupId } = props.scope.values;
  const scope = getScope(campaignId, adGroupId);

  const isHotelCampaign = currentCampaign && currentCampaign.CampaignType === 'Hotel';

  // Exclude Campaign, AdGroup status from props because settings pages don't need to change
  // based on status.
  const omittedAdGroupProps = [
    'Status',
    'EntityStatus',
    ...getIsNewShoppingWizard({ currentCampaign, permissions }) ? [] : ['AdRotation'],
  ];
  const filteredProps = {
    currentCampaign: (isHotelCampaign && scope === TargetingScope.Campaign) ? omit(formatCurrentCampaign, 'Name', 'BiddingScheme') : formatCurrentCampaign,
    currentAdGroup: currentAdGroup ? omit(currentAdGroup, ...omittedAdGroupProps) : currentAdGroup,
  };

  return {
    ...props,
    ...filteredProps,
  };
};

// Thin wrapper over the ContentFooter which, using { margin-top: 'auto' }
// makes footer move to the bottom of the container.
// While the actual settings page is loading, the footer will be
// at the bottom of the page. After page renders, it will expand
// the parent's height more than the view-port allows, which
// will collapse the margin above the footer, making it just follow
// the page contents.
const BottomStickingContentFooter = props => (
  <div style={{ marginTop: 'auto' }}>
    <ContentFooter {...props} />
  </div>
);

const lazyWrap = p$view => React.lazy(() => p$view.then(instance => ({ default: () => instance })));
class UnStyledAllSettings extends React.Component {
  componentDidMount() {
    const {
      currentCampaign, currentAdGroup, scope, siteMap,
    } = this.props;
    const adsPageRoutePath = getRoutePathById(siteMap, 'ads');
    redirectToAdsPageIfCurrentCampaignIsSSCAndUserIsInAdGroupLevel(scope, currentCampaign, currentAdGroup, adsPageRoutePath);
  }

  shouldComponentUpdate(nextProps, nextState) {
    // Currently there's no state in this component, so second half is just a guardrail.
    return !isEqual(nextProps, this.props) || nextState !== this.state;
  }

  componentWillUnmount() {
    if (this.backboneSettingsView) {
      this.backboneSettingsView.remove();
    }
  }

  isHotelCampaign() {
    const {
      currentCampaign,
      permissions,
    } = this.props;
    const { campaignId, adGroupId } = this.props.scope.values;
    const scope = getScope(campaignId, adGroupId);
    const isHotelEnabled = permissions && permissions.dynamic && permissions.dynamic.HotelCampaign;
    const isHotelCampaign = currentCampaign && currentCampaign.CampaignType === 'Hotel';
    return scope !== TargetingScope.Account && isHotelEnabled && isHotelCampaign;
  }

  isPerfMaxCampaign() {
    const {
      currentCampaign,
      permissions,
    } = this.props;
    const { campaignId, adGroupId } = this.props.scope.values;
    const isCampaignScope = campaignId && !adGroupId;
    const isPerfMaxCampaignEnabled = permissions && permissions.dynamic && permissions.dynamic.PerformanceMaxCampaigns;
    const isPerfMaxCampaign = currentCampaign && CampaignType[currentCampaign.CampaignType] === CampaignType.PerformanceMax;
    return isCampaignScope && isPerfMaxCampaignEnabled && isPerfMaxCampaign;
  }

  isXandrVideoCampaign() {
    const {
      currentCampaign,
      permissions,
    } = this.props;
    const isXandrVideoCampaignEnabled = permissions && permissions.dynamic && permissions.dynamic.XandrVideoCampaigns;
    const isXandrVideoCampaign = currentCampaign && currentCampaign.CampaignSubType === CampaignSubTypeMapping.BrandAwarenessVideoAds;
    return isXandrVideoCampaignEnabled && isXandrVideoCampaign;
  }

  isAppAdsCampaign() {
    const {
      currentCampaign,
      permissions,
    } = this.props;
    const isAppAdsCampaignEnabled = permissions && permissions.dynamic && permissions.dynamic.AppAdsCampaignPhaseOne;
    const isAppAdsCampaign = currentCampaign && CampaignType[currentCampaign.CampaignType] === CampaignType.App;
    return isAppAdsCampaignEnabled && isAppAdsCampaign;
  }

  isUnifiedAppAdsCampaign() {
    const {
      currentCampaign,
    } = this.props;
    const appCampaignSettings = currentCampaign && currentCampaign.CampaignSettings && currentCampaign.CampaignSettings.find(setting => setting['@odata.type'] === '#Model.AppSetting');
    const isUnifiedAppAdsCampaignEnabled = appCampaignSettings ? (appCampaignSettings.IsUniversalAppCampaign) : false;

    const isMobileAppStore = appCampaignSettings && (appCampaignSettings.AppStore === 'GoogleAppStore' || appCampaignSettings.AppStore === 'AppleAppStore');
    const isUnifiedAppAdsCampaign = currentCampaign && CampaignType[currentCampaign.CampaignType] === CampaignType.App;
    return (isUnifiedAppAdsCampaignEnabled || isMobileAppStore) && isUnifiedAppAdsCampaign;
  }

  isDisplayPlusNativeAdsCampaign() {
    const {
      currentCampaign,
      permissions,
    } = this.props;
    const isDisplayPlusEditEnabled = permissions && permissions.dynamic && permissions.dynamic.DisplayPlusEdit;
    const isAudienceCampaignType = currentCampaign && isAudienceCampaign(currentCampaign.CampaignType);
    const isNativeAdsCampaign = currentCampaign && isAudienceNativeAdsCampaign(currentCampaign.CampaignSubType);
    return isDisplayPlusEditEnabled && isAudienceCampaignType && isNativeAdsCampaign;
  }

  isXandrDisplayAdsCampaign() {
    const {
      currentCampaign,
      permissions,
    } = this.props;
    const isXandrDisplayAdsCampaignEnabled = permissions && permissions.dynamic && permissions.dynamic.XandrDisplayAdsCampaigns;
    const isXandrDisplayAdsCampaign = currentCampaign && currentCampaign.CampaignSubType === CampaignSubTypeMapping.DisplayAds;
    return isXandrDisplayAdsCampaignEnabled && isXandrDisplayAdsCampaign;
  }

  isCTVCampaign() {
    const { currentCampaign } = this.props;
    return this.isXandrVideoCampaign() && currentCampaign.BiddingScheme && currentCampaign.BiddingScheme.Type === 'ManualCpv';
  }

  isDealCampaign() {
    const { currentCampaign } = this.props;
    return this.isXandrVideoCampaign() && currentCampaign.IsDealCampaign;
  }

  isSimplifiedSponsoredCampaign() {
    const { currentCampaign, permissions } = this.props;
    const isSPAv2Enabled = permissions && permissions.IsSPAv2Enabled;
    const isDomesticSimplifiedChineseEnabled = permissions && permissions.IsDomesticSimplifiedChineseEnabled;
    const isSponsoredCampaign = currentCampaign && currentCampaign.CampaignSubType === CampaignSubTypeMapping.ShoppingSponsoredProductAd;

    if (isSponsoredCampaign && (isSPAv2Enabled && !isDomesticSimplifiedChineseEnabled)) {
      return !isMSStoreAdsCampaign(currentCampaign);
    }

    return false;
  }

  isNewShoppingWizard() {
    const { currentCampaign, permissions } = this.props;
    return getIsNewShoppingWizard({ currentCampaign, permissions });
  }

  isLinkedInCampaign() {
    const { currentCampaign, permissions } = this.props;
    const isLinkedInEnabled = permissions && permissions.dynamic && permissions.dynamic.LinkedInCampaigns;
    const isLinkedInCampaign = currentCampaign && currentCampaign.CampaignType === 'LinkedIn';
    return isLinkedInEnabled && isLinkedInCampaign;
  }

  isSearchRevamp() {
    const { currentCampaign, permissions } = this.props;
    const isSearchCampaignCreationEnabled = permissions && permissions.dynamic && permissions.dynamic.SearchCampaignCreationRevamp;
    const isSearchRevampPhase2Enabled = permissions && permissions.dynamic && permissions.dynamic.SearchRevampPhase2;
    const isSearchSearchCampaign = currentCampaign && isSearchCampaign(currentCampaign.CampaignType, currentCampaign.CampaignSubType);
    return isSearchCampaignCreationEnabled && isSearchRevampPhase2Enabled && isSearchSearchCampaign;
  }

  render() {
    this.p$legacyInit = initializeWithLegacyDependencies(this.props, getDeps);
    const { campaignId, adGroupId } = this.props.scope.values;
    const scope = getScope(campaignId, adGroupId);

    const { perfMarker, ccJsData, permissions } = this.props;
    perfMarker.willFetchFile();

    let LazyView = null;
    let pageContainerClassName = this.props.classes.pageContainer;
    let needsFooter = true;

    if (this.isHotelCampaign()) {
      switch (scope) {
        case TargetingScope.Campaign:
          LazyView = HotelCampaignSettings;
          break;
        case TargetingScope.AdGroup:
          LazyView = HotelAdGroupSettings;
          break;
        default:
          throw new Error('Unexpected targeting settings scope!');
      }
    } else if (this.isPerfMaxCampaign()) {
      setClarityTag({
        IsPmaxNorthStarSetting: permissions.dynamic.PmaxNorthStarSetting,
      });
      LazyView = permissions.dynamic.PmaxNorthStarSetting ? PerfMaxNorthStarSettingWizard : PerfMaxCampaignSettingsPage;
      pageContainerClassName = this.props.classes.pageContainerExpandingToParent;
      needsFooter = false;
    } else if (this.isXandrVideoCampaign()) {
      const isCTVAndDealToDisplayPlusEnabled = permissions.dynamic.CTVAndDealToDisplayPlus;
      if (isCTVAndDealToDisplayPlusEnabled && (this.isCTVCampaign() || this.isDealCampaign())) {
        switch (scope) {
          case TargetingScope.Campaign:
            LazyView = DisplayPlusEditCampaignWizard;
            break;
          case TargetingScope.AdGroup:
            LazyView = DisplayPlusEditAdGroupWizard;
            break;
          default:
            throw new Error('Unexpected targeting settings scope!');
        }
      } else {
        switch (scope) {
          case TargetingScope.Campaign:
            LazyView = XandrVideoCampaignSettingsPage;
            break;
          case TargetingScope.AdGroup:
            LazyView = XandrVideoAdGroupSettingsPage;
            break;
          default:
            throw new Error('Unexpected targeting settings scope!');
        }
      }
      pageContainerClassName = this.props.classes.pageContainerExpandingToParent;
      needsFooter = false;
    } else if (this.isDisplayPlusNativeAdsCampaign()) {
      setClarityTag({
        IsDisplayPlusAudienceNative: true,
        CampaignSubType: this.props.currentCampaign.CampaignSubType,
      });
      switch (scope) {
        case TargetingScope.Campaign:
          LazyView = DisplayPlusEditCampaignWizard;
          break;
        case TargetingScope.AdGroup:
          LazyView = DisplayPlusEditAdGroupWizard;
          break;
        default:
          throw new Error('Unexpected targeting settings scope!');
      }
      pageContainerClassName = this.props.classes.pageContainerExpandingToParent;
      needsFooter = false;
    } else if (this.isUnifiedAppAdsCampaign()) {
      LazyView = UnifiedAppEditCampaignSettingsWizard;
      pageContainerClassName = this.props.classes.pageContainerExpandingToParent;
      needsFooter = false;
    } else if (this.isAppAdsCampaign()) {
      LazyView = AppAdsCampaignEditPage;
      pageContainerClassName = this.props.classes.pageContainerExpandingToParent;
      needsFooter = false;
    } else if (this.isXandrDisplayAdsCampaign()) {
      switch (scope) {
        case TargetingScope.Campaign:
          LazyView = XandrDisplayAdsCampaignSettingsPage;
          break;
        case TargetingScope.AdGroup:
          LazyView = XandrDisplayAdsAdGroupSettingsPage;
          break;
        default:
          throw new Error('Unexpected targeting settings scope!');
      }
      pageContainerClassName = this.props.classes.pageContainerExpandingToParent;
      needsFooter = false;
    } else if (this.isSimplifiedSponsoredCampaign() && scope === TargetingScope.Campaign) {
      const cashbackSettings = result(this.props.currentCampaign, 'CampaignCashback');
      const isSPBCampaign = (cashbackSettings && cashbackSettings.IsPromotionsForBrands) === true;
      LazyView = props => <SponsoredCampaignSettingsPage {...props} isMran={!isSPBCampaign} />;
    } else if (this.isNewShoppingWizard()) {
      switch (scope) {
        case TargetingScope.Campaign:
          LazyView = props => <StandardShoppingCampaignWizard {...props} createScenario={CREATE_SCENARIO.UPDATE_CAMPAIGN} />;
          break;
        case TargetingScope.AdGroup:
          LazyView = props => <StandardShoppingCampaignWizard {...props} createScenario={CREATE_SCENARIO.UPDATE_ADGROUP} />;
          break;
        default:
          throw new Error('Unexpected targeting settings scope!');
      }
      pageContainerClassName = this.props.classes.pageContainerExpandingToParent;
      needsFooter = false;
    } else if (this.isLinkedInCampaign()) {
      switch (scope) {
        case TargetingScope.Campaign:
          LazyView = LinkedInCampaignSettingsWizard;
          break;
        case TargetingScope.AdGroup:
          LazyView = LinkedInAdGroupSettingsWizard;
          break;
        default:
          throw new Error('Unexpected targeting settings scope!');
      }
    } else if (this.isSearchRevamp()) {
      switch (scope) {
        case TargetingScope.Campaign:
          LazyView = SearchCampaignEditWizard;
          break;
        case TargetingScope.AdGroup:
          LazyView = SearchAdGroupEditWizard;
          break;
        default:
          throw new Error('Unexpected targeting settings scope!');
      }
      pageContainerClassName = this.props.classes.pageContainerExpandingToParent;
      needsFooter = false;
    } else {
      const view = this.p$legacyInit.then(({ deps }) => {
        const legacyView = new AllSettingsView({
          campaignId,
          adgroupId: adGroupId,
          scope,
          perfMarker,
          ccJsData,
          serverContext: this.props.serverContext,
          scopeServiceScope: this.props.scope,
          deps: {
            ...deps,
            router: getRouterDep(this.props.scope, this.props.siteMap),
            eventDelegator: new EventDelegator(),
            i18nNewDesign: this.props.i18n,
          },
        });

        this.backboneSettingsView = legacyView;

        const isAudienceAdGroupSettingsPage = deps.pageContext.CurrentCampaign.CampaignType === 8 &&
          deps.pageContext.CurrentAdGroup;
        const classes = isAudienceAdGroupSettingsPage ?
          [this.props.classes.settingsFormContainer, this.props.classes.audienceAdGroupPage] :
          [this.props.classes.settingsFormContainer];

        return (
          <div className={classes.join(' ')}>
            <BackboneInstanceWrapper managed instance={legacyView} />
          </div>
        );
      });

      LazyView = lazyWrap(view);
    }
    return (
      <React.Suspense fallback={null}>
        <div className={classNames('all-settings-page-container', pageContainerClassName)}>
          <LazyView {...this.props} />
          {needsFooter && <BottomStickingContentFooter />}
        </div>
      </React.Suspense>
    );
  }
}
UnStyledAllSettings.propTypes = {
  serverContext: PropTypes.shape({
    CurrentAccount: PropTypes.shape({}).isRequired,
    CurrentCustomer: PropTypes.shape({}).isRequired,
  }),
  scope: PropTypes.shape({
    values: {
      adGroupId: PropTypes.number,
      campaignId: PropTypes.number,
    },
  }).isRequired,
  siteMap: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
  permissions: PropTypes.shape({
    IsSPAv2Enabled: PropTypes.bool,
    IsDomesticSimplifiedChineseEnabled: PropTypes.bool,
    dynamic: PropTypes.shape({
      HotelCampaign: PropTypes.bool,
      PerformanceMaxCampaigns: PropTypes.bool,
      XandrVideoCampaigns: PropTypes.bool,
      XandrDisplayAdsCampaigns: PropTypes.bool,
      AppAdsCampaignPhaseOne: PropTypes.bool,
      PAAIMConsolidation: PropTypes.bool,
      DisplayPlusEdit: PropTypes.bool,
      LinkedInCampaign: PropTypes.bool,
      CTVAndDealToDisplayPlus: PropTypes.bool,
      UnifiedAppAds: PropTypes.bool,
    }),
  }),
  classes: PropTypes.shape({
    pageContainer: PropTypes.string.isRequired,
    settingsFormContainer: PropTypes.string.isRequired,
    audienceAdGroupPage: PropTypes.string.isRequired,
    pageContainerExpandingToParent: PropTypes.string.isRequired,
  }).isRequired,
  currentCampaign: PropTypes.shape({
    CampaignType: PropTypes.string,
    CampaignSubType: PropTypes.number,
    BiddingScheme: PropTypes.shape({
      Type: PropTypes.string,
    }),
    IsDealCampaign: PropTypes.bool,
  }),
  currentAdGroup: PropTypes.shape({}),
  preferenceService: PropTypes.shape({}).isRequired,
  perfMarker: perfMarkerPropTypes.isRequired,
  ccJsData: PropTypes.shape({}).isRequired,
  i18n: PropTypes.shape({
    getString: PropTypes.func,
  }).isRequired,
};

UnStyledAllSettings.defaultProps = {
  serverContext: {},
  permissions: {},
  currentCampaign: null,
  currentAdGroup: null,
};
const SettingsWithPropFilter = withCcJsData(withPropFilter(UnStyledAllSettings, propFilter));

export const StyledAllSettingsView = withDefaultStyles(
  withSiteMap(withCurrentExpandedScope(SettingsWithPropFilter)),
  getSettingsPageStyles
);

